<template>
  <div class="fuel-supply-import container">
    <b-button
      size="sm"
      variant="secondary"
      @click="downloadLayout"
      style="margin-bottom: 15px;"
    >
      Baixar Layout de Importação
    </b-button>
    <b-form @submit.prevent="startImport">
      <b-form-group
        label="Selecione o arquivo Excel (.xlsx)"
        label-for="file-input"
      >
        <div class="custom-file">
          <input
            type="file"
            class="custom-file-input"
            id="file-input"
            @change="onFileChange"
            accept=".xlsx"
            required
          />
          <label class="custom-file-label" for="file-input">
            {{ selectedFileName || "Escolha um arquivo..." }}
          </label>
        </div>
      </b-form-group>
      <div class="d-flex justify-content-end">
        <b-button
          size="sm"
          type="submit"
          variant="primary"
          :disabled="!selectedFile || isImporting"
        >
          Iniciar Importação
        </b-button>
        <b-button
          size="sm"
          variant="danger"
          class="ml-2"
          @click="cancelImport"
          :disabled="!importId || !isImporting"
        >
          Cancelar Importação
        </b-button>
      </div>
    </b-form>

    <div class="mt-4" v-if="isImporting || progressMessages.length > 0">
      <div
        v-if="isLoading"
        style="display: flex; align-items: center; justify-content: center;"
      >
        <div class="loader"></div>
        <div
          :style="{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: '8px',
            marginBottom: '20px',
            fontSize: '20px',
            color: this.$store.state.darkmode ? 'white' : 'black'
          }"
        >
          Processo de Importação Iniciado...
        </div>
      </div>
      <b-progress
        max="100"
        animated
        striped
        class="mb-3"
        height="20px"
        variant="green"
      >
        <b-progress-bar
          :value="progressPercentage"
          class="font-weight-bold d-flex align-items-center justify-content-center"
        >
          {{ progressPercentage }}%
        </b-progress-bar>
      </b-progress>

      <b-table
        :items="progressMessages"
        :fields="tableFields"
        small
        responsive
        hover
      >
        <template #cell(status)="data">
          <b-badge :variant="getBadgeVariant(data.item.status)">
            {{ getStatusText(data.item.status) }}
          </b-badge>
        </template>
        <template #cell(message)="data">
          <span v-if="data.item.status === 'error'">
            {{ data.item.message }}
          </span>
          <span v-else>
            {{ data.item.message }}
          </span>
        </template>
      </b-table>
    </div>
  </div>
</template>

<script>
import * as signalR from "@microsoft/signalr";

export default {
  name: "FuelSupplyImport",
  data() {
    return {
      isLoading: false,
      selectedFile: null,
      selectedFileName: "",
      importId: null,
      progressPercentage: 0,
      progressMessages: [],
      connection: null,
      isImporting: false,
      tableFields: [
        { key: "lineNumber", label: "Linha" },
        { key: "status", label: "Status" },
        { key: "message", label: "Mensagem" }
      ]
    };
  },
  methods: {
    onFileChange(event) {
      const file = event.target.files[0];
      this.selectedFile = file;
      this.selectedFileName = file ? file.name : "";
    },
    startImport() {
      this.isLoading = true;
      if (!this.selectedFile) return;

      const formData = new FormData();
      formData.append("file", this.selectedFile);

      this.isImporting = true;

      this.$apiVehicle
        .post("/importstation/start", formData, {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        })
        .then(response => {
          this.importId = response.data.importId;
          this.setupSignalRConnection();
        })
        .catch(error => {
          console.error("Erro ao iniciar a importação:", error);
          this.isImporting = false;
          this.isLoading = false;
        });
    },
    cancelImport() {
      if (!this.importId) return;

      this.$apiVehicle
        .post(`/importstation/cancel/${this.importId}`)
        .then(() => {
          if (this.connection) {
            this.connection.stop();
          }
          this.importId = null;
          this.isImporting = false;
          this.isLoading = false;
        })
        .catch(error => {
          console.error("Erro ao cancelar a importação:", error);
        });
    },
    setupSignalRConnection() {
      let baseUrl = this.$apiVehicle.defaults.baseURL;
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(`${baseUrl}/importHub`, {
          accessTokenFactory: () => this.$store.state.token,
          transport: signalR.HttpTransportType.LongPolling,
          withCredentials: false,
          mode: "no-cors"
        })
        .withAutomaticReconnect()
        .configureLogging(signalR.LogLevel.Information)
        .build();

      this.connection
        .start()
        .then(() => {
          this.connection
            .invoke("JoinGroup", this.importId)
            .then(() => {
              console.log("Conectado ao grupo:", `UserImport_${this.importId}`);
            })
            .catch(error => {
              console.error("Erro ao ingressar no grupo:", error);
            });

          this.connection.on("ReceiveProgress", message => {
            this.processProgressMessage(message);
          });
        })
        .catch(error => {
          console.error("Erro ao conectar ao SignalR:", error);
          this.isImporting = false;
          this.isLoading = false;
        });
    },
    processProgressMessage(message) {
      console.log("Mensagem recebida:", message);

      if (message.startsWith("Progresso:")) {
        const percentMatch = message.match(/Progresso: (\d+)%/);
        if (percentMatch) {
          this.progressPercentage = parseInt(percentMatch[1]);
        }
      } else if (message.startsWith("Erro na linha")) {
        const errorMatch = message.match(/Erro na linha (\d+): (.+)/);
        if (errorMatch) {
          const lineNumber = parseInt(errorMatch[1]);
          const errorMessage = errorMatch[2];
          this.progressMessages.push({
            lineNumber,
            status: "error",
            message: `Erro: ${errorMessage}`
          });
        }
      } else if (message.startsWith("Sucesso:")) {
        const successMatch = message.match(/Sucesso: (\d+)/);
        if (successMatch) {
          const lineNumber = parseInt(successMatch[1]);
          this.progressMessages.push({
            lineNumber,
            status: "success",
            message: "Importada com sucesso."
          });
        }
      } else if (
        message === "Importação finalizada com sucesso." ||
        message === "Importação cancelada."
      ) {
        this.progressPercentage = 100;
        const status =
          message === "Importação finalizada com sucesso."
            ? "success"
            : "warning";
        this.progressMessages.push({ lineNumber: "-", status, message });
        if (this.connection) {
          this.connection.stop();
        }
        this.importId = null;
        this.isImporting = false;
        this.isLoading = false;
      } else {
        this.progressMessages.push({
          lineNumber: "-",
          status: "info",
          message
        });
      }
    },
    getBadgeVariant(status) {
      switch (status) {
        case "success":
          return "success";
        case "error":
          return "danger";
        case "warning":
          return "warning";
        case "info":
        default:
          return "info";
      }
    },
    getStatusText(status) {
      switch (status) {
        case "success":
          return "Sucesso";
        case "error":
          return "Erro";
        case "warning":
          return "Cancelado";
        case "info":
        default:
          return "Informação";
      }
    },
    downloadLayout() {
      this.$apiVehicle
        .request({
          url: `/importstation/download/`,
          method: "GET",
          responseType: "arraybuffer"
        })
        .then(response => {
          const downloadUrl = window.URL.createObjectURL(
            new Blob([response.data], {
              type: response.headers["content-type"]
            })
          );
          const link = document.createElement("a");
          link.href = downloadUrl;
          link.setAttribute("download", response.headers["filename"]);
          document.body.appendChild(link);
          link.click();
          link.remove();
          this.$refs.openModal.hide();
        });
    }
  }
};
</script>

<style scoped>
.fuel-supply-import {
  max-width: 1000px;
  margin: 0 auto;
}

.b-card {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
.custom-file-input ~ .custom-file-label::after {
  content: "Procurar";
}
.loader {
  border: 6px solid #cccccc;
  border-top: 6px solid greenyellow;
  border-radius: 50%;
  width: 25px;
  height: 25px;
  animation: spin 1s linear infinite;
  margin-right: 8px;
  margin-bottom: 20px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
